/*
  component-shipping-notice.js（Split 4.4.0 標準を拡張・陶庫 2026-09-24）
  - 残額は /cart.js の total_price から計算する
  - 無料ラインはテーマ設定の通常／キャンペーン金額を、キャンペーン期間（日本時間 YYYY-MM-DD）で自動切替
  - /cart/add.js・change.js・update.js・clear.js への fetch / XHR を監視し、完了後に再計算する
    （ギフト設定アプリ等、テーマのイベントを発火しない外部スクリプトによる明細追加に対応）
  - カート上部の帯の金額 [data-js-free-shipping-amount] も同じ判定で更新する
  - 文言は KROWN.settings.locales（locales/ja.json・en.default.json の cart.free_shipping_*）
*/
if ( typeof ShippingNotice !== 'function' ) {

  const FS_JST_OFFSET_MS = 9 * 60 * 60 * 1000;
  const FS_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
  const FS_CART_MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js)?([?#]|$)/;

  const FreeShipping = {

    _timer: null,
    _lastTotal: null,

    todayJST() {
      return new Date(Date.now() + FS_JST_OFFSET_MS).toISOString().slice(0, 10);
    },

    isCampaignActive(start, end) {
      if ( ! FS_DATE_RE.test(start || '') || ! FS_DATE_RE.test(end || '') ) return false;
      const today = this.todayJST();
      return start <= today && today <= end;
    },

    currencyRate() {
      try {
        return ( window.Shopify && Shopify.currency && Shopify.currency.rate ) ? Number(Shopify.currency.rate) : 1;
      } catch (e) {
        return 1;
      }
    },

    // 有効な無料ライン（最小通貨単位 = 円×100）
    threshold(el) {
      const regular = Number(el.getAttribute('data-free-shipping')) || 0;
      const campaign = Number(el.getAttribute('data-free-shipping-campaign')) || 0;
      const active = campaign > 0 && this.isCampaignActive(el.getAttribute('data-campaign-start'), el.getAttribute('data-campaign-end'));
      return Math.round(( active ? campaign : regular ) * this.currencyRate());
    },

    fill(template, amount) {
      return String(template || '').replace(/\{\{\s*(remaining_amount|amount)\s*\}\}/g, amount);
    },

    renderNotice(el, total) {
      const textEl = el.querySelector('[data-js-free-shipping-text]');
      const bar = el.querySelector('[data-js-free-shipping-slider]');
      if ( ! textEl ) return false;
      const threshold = this.threshold(el);
      if ( threshold <= 0 ) return true;
      const remaining = threshold - total;
      let width;
      if ( remaining > 0 ) {
        textEl.innerHTML = this.fill(KROWN.settings.locales.shipping_notice_remaining_to_free, this.formatMoney(remaining, KROWN.settings.shop_money_format));
        width = Math.max(0, Math.min(100, 100 - ( remaining * 100 / threshold )));
      } else {
        textEl.innerHTML = KROWN.settings.locales.shipping_notice_eligible_for_free;
        width = 100;
      }
      if ( bar ) bar.style.width = `${width}%`;
      el.setAttribute('data-cart-total', total);
      el.setAttribute('data-threshold-active', threshold);
      return true;
    },

    renderAmounts() {
      document.querySelectorAll('[data-js-free-shipping-amount]').forEach(el => {
        const threshold = this.threshold(el);
        if ( threshold > 0 ) {
          el.textContent = this.formatMoney(threshold, KROWN.settings.shop_money_format);
        }
      });
    },

    renderAll(total) {
      this._lastTotal = total;
      document.querySelectorAll('shipping-notice').forEach(el => this.renderNotice(el, total));
      this.renderAmounts();
    },

    refresh() {
      return fetch(`${KROWN.settings.routes.cart_url}.js`, { credentials: 'same-origin', cache: 'no-store', headers: { 'Accept': 'application/json' } })
        .then(response => response.json())
        .then(cart => {
          if ( cart && typeof cart.total_price === 'number' ) {
            this.renderAll(cart.total_price);
          }
        })
        .catch(() => {});
    },

    scheduleRefresh(delay = 150) {
      clearTimeout(this._timer);
      this._timer = setTimeout(() => this.refresh(), delay);
    },

    // /cart/add.js 等の完了を監視して再計算（fetch と XHR の両方）
    installWatchers() {
      if ( window.__tokoFreeShippingWatching ) return;
      window.__tokoFreeShippingWatching = true;

      const originalFetch = window.fetch;
      if ( typeof originalFetch === 'function' ) {
        window.fetch = function(input, init) {
          const result = originalFetch.apply(this, arguments);
          try {
            const url = typeof input === 'string' ? input : ( input && input.url ) || '';
            if ( FS_CART_MUTATION_RE.test(url) ) {
              result.then(() => FreeShipping.scheduleRefresh(), () => {});
            }
          } catch (e) {}
          return result;
        };
      }

      if ( window.XMLHttpRequest ) {
        const originalOpen = XMLHttpRequest.prototype.open;
        XMLHttpRequest.prototype.open = function(method, url) {
          try {
            if ( FS_CART_MUTATION_RE.test(String(url)) ) {
              this.addEventListener('loadend', () => FreeShipping.scheduleRefresh());
            }
          } catch (e) {}
          return originalOpen.apply(this, arguments);
        };
      }

      // テーマ標準のカート更新イベント（cart-form から非バブリングで発火するため capture で受ける）
      document.addEventListener('cart-updated', () => FreeShipping.scheduleRefresh(), true);

      // アプリ向けフック window.refreshCart（component-cart.js）が呼ばれた後も再計算
      if ( typeof window.refreshCart === 'function' && ! window.refreshCart.__tokoWrapped ) {
        const originalRefreshCart = window.refreshCart;
        const wrapped = function() {
          const r = originalRefreshCart.apply(this, arguments);
          FreeShipping.scheduleRefresh(400);
          return r;
        };
        wrapped.__tokoWrapped = true;
        window.refreshCart = wrapped;
      }
    },

    formatMoney(cents, format) {

      if ( typeof cents === 'string' ) {
        cents = cents.replace('.', '');
      }

      let value = '';
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const formatString = format || '¥{{amount_no_decimals}}';

      function formatWithDelimiters(number, precision, thousands, decimal) {
        thousands = thousands || ',';
        decimal = decimal || '.';

        if ( isNaN(number) || number === null ) {
          return 0;
        }

        number = ( number / 100.0 ).toFixed(precision);

        const parts = number.split('.');
        const dollarsAmount = parts[0].replace(
          /(\d)(?=(\d\d\d)+(?!\d))/g,
          '$1' + thousands
        );
        const centsAmount = parts[1] ? decimal + parts[1] : '';

        return dollarsAmount + centsAmount + ( KROWN.settings.iso_code || '' );
      }

      const match = formatString.match(placeholderRegex);
      switch ( match ? match[1] : 'amount_no_decimals' ) {
        case 'amount':
          value = formatWithDelimiters(cents, 2);
          break;
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0);
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ',');
          break;
        case 'amount_no_decimals_with_space_separator':
          value = formatWithDelimiters(cents, 0, ' ');
          break;
        case 'amount_with_apostrophe_separator':
          value = formatWithDelimiters(cents, 2, "'");
          break;
        default:
          value = formatWithDelimiters(cents, 0);
      }

      return match ? formatString.replace(placeholderRegex, value) : value;

    }

  };

  window.TOKOFreeShipping = FreeShipping;

  class ShippingNotice extends HTMLElement {

    constructor() {
      super();
    }

    connectedCallback() {
      // Liquid が出力した合計で先に描画し、その後 /cart.js の合計で確定させる
      const initial = Number(this.getAttribute('data-cart-total'));
      const rendered = FreeShipping.renderNotice(this, isNaN(initial) ? 0 : initial);
      if ( ! rendered ) {
        // 子要素がまだ無い場合（パーサー生成直後）は次フレームで再試行
        requestAnimationFrame(() => FreeShipping.renderNotice(this, isNaN(initial) ? 0 : initial));
      }
      FreeShipping.scheduleRefresh();
    }

  }

  if ( typeof customElements.get('shipping-notice') == 'undefined' ) {
    customElements.define('shipping-notice', ShippingNotice);
  }

  const fsStart = () => {
    FreeShipping.installWatchers();
    FreeShipping.renderAmounts();
    FreeShipping.scheduleRefresh();
  };
  if ( document.readyState === 'loading' ) {
    document.addEventListener('DOMContentLoaded', fsStart);
  } else {
    fsStart();
  }

}
